import React, {useContext, useState} from "react";
import "./ContentCustomer.scss";
import {EventUpdatedContext} from "../../../store/EventUpdatedContext";
import {useFetchCustomers, DeleteCustomer} from "../../../service/Business/CustomerService";
import {getIdWithToken} from "../../../utils/storage/Token";
import FCSpinner from "../../FCObjets/FCSpinner";
import {TypeForm} from "../../../types/EnumTypeForm";
import {CustomerInterface} from "../../../service/Business/Interfaces/CustomerInterface";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import FCContentModalFL, {DataForm} from "../../FCObjets/Form/FCContentModalFL";
import {useToast} from "../../FCObjets/FCToast/FCToast";


function ContentCustomer() {
    const toast = useToast();

    let {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: customers, loading, error} = useFetchCustomers(getIdWithToken(), cptUpdated);
    const [searchQuery, setSearchQuery] = useState("");
    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    /**
     * Gestion de l'ouverture du modal
     */
    const gestionModal = (open: boolean, isConfirm: boolean, e: CustomerInterface | any | undefined) => {
        let t = TypeForm.EMPTY;
        if (open) {
            if (isConfirm) {
                t = TypeForm.CONFIRM;
            } else {
                t = TypeForm.CUSTOMER;
            }
        }

        let r: DataForm = {
            type: t,
            data: e
        };

        setData(r);
    }

    /**
     * Filtre les clients par recherche
     */
    const filteredCustomers = customers
        ? customers.filter((c: CustomerInterface) =>
            c.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.siret.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.address.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    /**
     * Formate un SIRET pour l'affichage
     */
    const formatSiret = (siret: string): string => {
        if (!siret) return "";
        const clean = siret.replace(/\s/g, "");
        return clean.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
    };

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => {
            gestionModal(false, false, undefined);
        }}>
            <div className="customerPage">
                {/* Header */}
                <div className="customerHeader">
                    <div className="customerHeaderLeft">
                        <h1 className="customerTitle">Liste des Clients</h1>
                        <p className="customerSubtitle">Gérez votre portefeuille de clients et suivez vos collaborations.</p>
                    </div>
                    <div className="customerHeaderRight">
                        {/* Barre de recherche */}
                        <div className="customerSearchBar">
                            <span className="material-symbols-outlined customerSearchIcon">search</span>
                            <input
                                type="text"
                                className="customerSearchInput"
                                placeholder="Rechercher un client..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button className="customerBtnAdd" onClick={() => gestionModal(true, false, undefined)}>
                            <span className="material-symbols-outlined">person_add</span>
                            Ajouter un client
                        </button>
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="customerLoading">
                        <FCSpinner size="large"/>
                    </div>
                )}

                {/* Error */}
                {error && <FCAlertMsgErrorAPI error={error}/>}

                {/* Stats footer */}
                {!loading && customers && customers.length > 0 && (
                    <div className="customerStats">
                        <div className="customerStatCard">
                            <div className="customerStatIcon">
                                <span className="material-symbols-outlined">trending_up</span>
                            </div>
                            <div className="customerStatValue">{customers.length}</div>
                            <div className="customerStatLabel">Clients Totaux</div>
                        </div>
                    </div>
                )}

                {/* Liste des clients */}
                {!loading && !error && (
                    <div className="customerList">
                        {filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer: CustomerInterface, index: React.Key) => (
                                <div className="customerCard" key={index}>
                                    <div className="customerCardMain">
                                        <div className="customerCardInfo">
                                            <div className="customerCardIcon">
                                                <span className="material-symbols-outlined">apartment</span>
                                            </div>
                                            <div className="customerCardDetails">
                                                <div className="customerCardNameRow">
                                                    <h3 className="customerCardName">{customer.companyName}</h3>
                                                    <span className="customerCardBadge">Actif</span>
                                                </div>
                                                <div className="customerCardSiret">{formatSiret(customer.siret)}</div>
                                                {customer.address && (
                                                    <div className="customerCardAddress">
                                                        <span className="material-symbols-outlined customerCardAddressIcon">location_on</span>
                                                        {customer.address}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="customerCardActions">
                                            <button className="customerActionBtn customerActionEdit"
                                                    onClick={() => gestionModal(true, false, customer)}
                                                    title="Modifier">
                                                <span className="material-symbols-outlined">edit</span>
                                            </button>
                                            <button className="customerActionBtn customerActionDelete"
                                                    onClick={() => {
                                                        const onConfirm = () => {
                                                            DeleteCustomer(getIdWithToken(), customer).then(() => {
                                                                toast.success("Client supprimé.", "Client");
                                                                setUpdated();
                                                            });
                                                        };
                                                        const payload = {
                                                            title: `Suppression de ${customer.companyName}`,
                                                            msg: "Êtes-vous sûr de vouloir supprimer ce client ?",
                                                            onConfirm: onConfirm
                                                        };
                                                        gestionModal(true, true, payload);
                                                    }}
                                                    title="Supprimer">
                                                <span className="material-symbols-outlined">delete</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mobile actions */}
                                    <div className="customerCardMobileActions">
                                        <button className="customerMobileBtn customerMobileBtnEdit"
                                                onClick={() => gestionModal(true, false, customer)}>
                                            <span className="material-symbols-outlined">edit</span>
                                            Modifier
                                        </button>
                                        <button className="customerMobileBtn customerMobileBtnDelete"
                                                onClick={() => {
                                                    const onConfirm = () => {
                                                        DeleteCustomer(getIdWithToken(), customer).then(() => {
                                                            toast.success("Client supprimé.", "Client");
                                                            setUpdated();
                                                        });
                                                    };
                                                    const payload = {
                                                        title: `Suppression de ${customer.companyName}`,
                                                        msg: "Êtes-vous sûr de vouloir supprimer ce client ?",
                                                        onConfirm: onConfirm
                                                    };
                                                    gestionModal(true, true, payload);
                                                }}>
                                            <span className="material-symbols-outlined">delete</span>
                                            Supprimer
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="customerEmpty">
                                <span className="material-symbols-outlined customerEmptyIcon">group_add</span>
                                <h3 className="customerEmptyTitle">Nouveau Partenaire</h3>
                                <p className="customerEmptyDesc">Développez votre activité en ajoutant un nouveau client.</p>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </FCContentModalFL>
    );
}

export default ContentCustomer;
